/* Hands-free voice conversation engine: VAD (silence detection) + barge-in interruption. */
export class VoiceEngine {
  constructor({ onUtterance, onState }) {
    this.onUtterance = onUtterance;
    this.onState = onState;
    this.phase = 'idle'; // listening | speaking | processing
    this.stopped = false;
    this.speechStarted = false;
    this.silenceMs = 0;
    this.voiceMs = 0;
    this.lastTick = 0;
    this.stream = null;
    this.ctx = null;
    this.analyser = null;
    this.recorder = null;
    this.chunks = [];
    this.audio = null;
    this.source = null;
    this.raf = null;
  }

  async init() {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    const AC = window.AudioContext || window.webkitAudioContext;
    this.ctx = new AC();
    try { await this.ctx.resume(); } catch {}
    const src = this.ctx.createMediaStreamSource(this.stream);
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 1024;
    src.connect(this.analyser);
    this.buf = new Uint8Array(this.analyser.fftSize);
    this.loop();
  }

  rms() {
    this.analyser.getByteTimeDomainData(this.buf);
    let sum = 0;
    for (let i = 0; i < this.buf.length; i++) {
      const v = (this.buf[i] - 128) / 128;
      sum += v * v;
    }
    return Math.sqrt(sum / this.buf.length);
  }

  loop = () => {
    if (this.stopped) return;
    const now = performance.now();
    const dt = this.lastTick ? Math.min(now - this.lastTick, 100) : 16;
    this.lastTick = now;
    const level = this.rms();
    const TH = 0.025;

    if (this.phase === 'listening') {
      if (level > TH) {
        this.voiceMs += dt;
        this.silenceMs = 0;
        if (this.voiceMs > 150 && !this.speechStarted) {
          this.speechStarted = true;
          this.onState('hearing');
        }
      } else {
        this.silenceMs += dt;
        this.voiceMs = 0;
      }
      if (this.speechStarted && this.silenceMs > 1700) this.finishUtterance();
    } else if (this.phase === 'speaking') {
      if (level > TH * 1.7) this.voiceMs += dt;
      else this.voiceMs = 0;
      if (this.voiceMs > 400) this.interrupt();
    }
    this.raf = requestAnimationFrame(this.loop);
  };

  startListening(speechAlreadyStarted = false) {
    if (this.stopped) return;
    if (this.ctx && this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    this.phase = 'listening';
    this.speechStarted = speechAlreadyStarted;
    this.silenceMs = 0;
    this.voiceMs = 0;
    this.chunks = [];
    this.recorder = new MediaRecorder(this.stream);
    this.recorder.ondataavailable = (e) => e.data.size && this.chunks.push(e.data);
    this.recorder.start();
    this.onState(speechAlreadyStarted ? 'hearing' : 'listening');
  }

  finishUtterance() {
    this.phase = 'processing';
    const rec = this.recorder;
    if (!rec || rec.state !== 'recording') return;
    rec.onstop = () => {
      const blob = new Blob(this.chunks, { type: rec.mimeType || 'audio/webm' });
      this.onUtterance(blob);
    };
    rec.stop();
    this.onState('thinking');
  }

  /* Play the specialist's reply; user can barge in. resume=false ends the loop after playback.
     Uses Web Audio (already unlocked by the mic gesture) — mobile browsers block fresh
     HTMLAudio.play() calls without a user gesture, which silenced replies after the first. */
  async speak(audioUrl, { resume = true } = {}) {
    if (this.stopped) return;
    this.phase = 'speaking';
    this.voiceMs = 0;
    this.resumeAfterSpeak = resume;
    this.onState('speaking');
    if (this.ctx.state === 'suspended') {
      try { await this.ctx.resume(); } catch {}
    }
    return new Promise((resolve) => {
      const finish = () => {
        if (!this.stopped && this.phase === 'speaking') {
          if (this.resumeAfterSpeak) this.startListening();
          else { this.phase = 'idle'; this.onState('complete'); }
        }
        resolve();
      };
      const playViaHtmlAudio = () => {
        const audio = new Audio(audioUrl);
        this.audio = audio;
        audio.onended = () => { this.audio = null; finish(); };
        audio.play().catch(() => { this.audio = null; finish(); });
      };
      (async () => {
        try {
          const b64 = audioUrl.split(',')[1];
          const bin = atob(b64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          const buffer = await this.ctx.decodeAudioData(bytes.buffer);
          if (this.stopped || this.phase !== 'speaking') { resolve(); return; }
          const src = this.ctx.createBufferSource();
          src.buffer = buffer;
          src.connect(this.ctx.destination);
          this.source = src;
          src.onended = () => {
            if (this.source === src) { this.source = null; finish(); }
            else resolve();
          };
          src.start();
        } catch {
          playViaHtmlAudio();
        }
      })();
    });
  }

  interrupt() {
    if (this.source) {
      const src = this.source;
      this.source = null;
      src.onended = null;
      try { src.stop(); } catch {}
    }
    if (this.audio) {
      this.audio.onended = null;
      this.audio.pause();
      this.audio = null;
    }
    if (!this.stopped) this.startListening(true);
  }

  stop() {
    this.stopped = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    try { if (this.recorder && this.recorder.state === 'recording') { this.recorder.onstop = null; this.recorder.stop(); } } catch {}
    if (this.source) { const s = this.source; this.source = null; s.onended = null; try { s.stop(); } catch {} }
    if (this.audio) { this.audio.onended = null; this.audio.pause(); this.audio = null; }
    this.stream?.getTracks().forEach((t) => t.stop());
    try { this.ctx?.close(); } catch {}
  }
}
